import { Fragment, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, Dropdown, Label, Menu } from '@/components/ui/daisy';
import {
  CheckIcon,
  PencilSquareIcon,
  PhotoIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { EllipsisVerticalIcon } from '@heroicons/react/16/solid';

const SHARED_KEY_PREFIX =
  'courseLineForm.questionsTab.createQuestionModal.questionForm';

type AnswerMoreOptionsProps = {
  children: ReactNode;
};

const AnswerMoreOptionsRoot = ({ children }: AnswerMoreOptionsProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'common',
  });

  return (
    <Dropdown position="bottom" align="end">
      <Dropdown.Trigger>
        <Button
          variant="ghost"
          size="xs"
          type="button"
          shape="circle"
          aria-label={t('moreOptions')}
        >
          <EllipsisVerticalIcon className="size-4.5" />
        </Button>
      </Dropdown.Trigger>
      <Dropdown.Content>
        <Menu
          className="bg-base-100 shadow-lg rounded-box w-40"
          activeColor="neutral"
        >
          {children}
        </Menu>
      </Dropdown.Content>
    </Dropdown>
  );
};

type EditActionProps = {
  onClick: () => void;
};

type EditActionVariantProps = EditActionProps & {
  icon: ReactNode;
  labelKey: 'editText' | 'editMedia';
};

const EditAction = ({ icon, labelKey, onClick }: EditActionVariantProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: SHARED_KEY_PREFIX,
  });

  return (
    <Menu.Item>
      <button
        type="button"
        className="flex items-center gap-2 text-base-content"
        onClick={onClick}
      >
        {icon}
        {t(labelKey)}
      </button>
    </Menu.Item>
  );
};

const EditTextAction = (props: EditActionProps) => (
  <EditAction
    icon={<PencilSquareIcon className="w-4 h-4" />}
    labelKey="editText"
    {...props}
  />
);

const EditMediaAction = (props: EditActionProps) => (
  <EditAction
    icon={<PhotoIcon className="w-4 h-4" />}
    labelKey="editMedia"
    {...props}
  />
);

type MarkActionProps = {
  onClick: () => void;
};

const MarkCorrectAction = ({ onClick }: MarkActionProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: SHARED_KEY_PREFIX,
  });

  return (
    <Menu.Item>
      <button
        type="button"
        className="flex items-center gap-2 text-success"
        onClick={onClick}
      >
        <CheckIcon className="w-4 h-4 shrink-0" />
        {t('markCorrect')}
      </button>
    </Menu.Item>
  );
};

const MarkIncorrectAction = ({ onClick }: MarkActionProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: SHARED_KEY_PREFIX,
  });

  return (
    <Menu.Item>
      <button
        type="button"
        className="flex items-center gap-2 text-error"
        onClick={onClick}
      >
        <XMarkIcon className="w-4 h-4 shrink-0" />
        {t('markIncorrect')}
      </button>
    </Menu.Item>
  );
};

type ToggleCorrectActionProps = {
  isCorrect: boolean;
  onClick: () => void;
};

const ToggleCorrectAction = ({
  isCorrect,
  onClick,
}: ToggleCorrectActionProps) => {
  if (isCorrect) {
    return <MarkIncorrectAction onClick={onClick} />;
  }
  return <MarkCorrectAction onClick={onClick} />;
};

type DeleteActionProps = {
  onClick: () => void;
};

const DeleteAction = ({ onClick }: DeleteActionProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: SHARED_KEY_PREFIX,
  });

  return (
    <Menu.Item>
      <button
        type="button"
        className="flex items-center gap-2 text-error"
        onClick={onClick}
      >
        <TrashIcon className="w-4 h-4" />
        {t('delete')}
      </button>
    </Menu.Item>
  );
};

type PositionsActionProps = {
  totalPositions: number;
  selectedPositions: number[];
  onChange: (positions: number[]) => void;
  header: string;
  getPositionLabel: (position: number) => string;
};

const PositionsAction = ({
  totalPositions,
  selectedPositions,
  onChange,
  header,
  getPositionLabel,
}: PositionsActionProps) => {
  const handleToggle = (position: number) => {
    const next = selectedPositions.includes(position)
      ? selectedPositions.filter(p => p !== position)
      : [...selectedPositions, position];
    const uniquePositions = Array.from(new Set(next));
    const orderedPositions = uniquePositions.sort((a, b) => a - b);
    onChange(orderedPositions);
  };

  return (
    <Fragment>
      <div className="flex flex-col gap-2 px-2 py-1">
        <span className="text-xs font-semibold text-base-content">
          {header}
        </span>
        <Checkbox.Group className="flex flex-col gap-1">
          {Array.from({ length: totalPositions }, (_, i) => i + 1).map(
            position => (
              <Label key={position} className="text-base-content/70">
                <Checkbox
                  size="sm"
                  checked={selectedPositions.includes(position)}
                  onChange={() => handleToggle(position)}
                />
                {getPositionLabel(position)}
              </Label>
            ),
          )}
        </Checkbox.Group>
      </div>
    </Fragment>
  );
};

export const AnswerMoreOptions = Object.assign(AnswerMoreOptionsRoot, {
  EditText: EditTextAction,
  EditMedia: EditMediaAction,
  MarkCorrect: MarkCorrectAction,
  MarkIncorrect: MarkIncorrectAction,
  ToggleCorrect: ToggleCorrectAction,
  Positions: PositionsAction,
  Delete: DeleteAction,
});
